"use client";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripeElementLocale, StripePaymentElementOptions } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import type { ReactNode, SyntheticEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Price } from "@calcom/features/bookings/components/event-meta/Price";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import type { PaymentOption } from "@calcom/prisma/enums";
import { Button } from "@calcom/ui/components/button";

import type { StripePaymentErrorKey } from "./stripeErrorMessages";
import { getStripePaymentErrorKey } from "./stripeErrorMessages";
import type { PollableStatus } from "./useStripePaymentStatusPolling";
import { useStripePaymentStatusPolling } from "./useStripePaymentStatusPolling";

/**
 * PAY-E1 — the single, shared inline Stripe payment surface, mounted **inside the Booker** (US 1.1)
 * and reused by the `/payment/[uid]` fallback (US 1.7). Written from scratch (no `ee/payments` code).
 *
 * - Multi-tenant Connect: `loadStripe(publishableKey, { stripeAccount })` **directly** (never the
 *   `getStripe` singleton, which drops `stripeAccount` and memoises the first key). Cf. epic-1 §225.
 * - PCI: the card never touches our servers — tokenisation is 100% Stripe Elements.
 * - Confirmation authority stays server-side (PAY-E2); on `succeeded` we only signal `onPaid`.
 */

export type PaymentUiStatus = "idle" | "processing" | "validating" | "success" | "error" | "timeout";

export interface StripeInlinePaymentProps {
  clientSecret: string;
  publishableKey: string;
  stripeAccount: string;
  amount: number;
  currency: string;
  paymentOption: PaymentOption | null;
  /** URL Stripe returns to for redirect-based auth (3DS / async). Carries the paymentUid, never the secret. */
  returnUrl: string;
  /** Optional recap block (what / when / where) rendered above the amount + card field (US 1.2). */
  recap?: ReactNode;
  /** Force a theme; when omitted the current `dark` class on <html> is used. */
  isDarkTheme?: boolean;
  /** Signalled once the payment is confirmed succeeded (server-confirmed via the poller when available). */
  onPaid?: () => void;
  /** Optional status reader enabling the bounded US 1.8 fallback poller while validating. */
  pollStatus?: () => Promise<PollableStatus>;
  /** Where the patient can follow their booking if server confirmation outlasts the poller (timeout screen). */
  bookingHref?: string;
}

// `layout` is supported at runtime but missing from this stripe-js version's option type → cast.
const PAYMENT_ELEMENT_OPTIONS = { layout: "accordion" } as StripePaymentElementOptions;

function StripeInlinePaymentForm(props: StripeInlinePaymentProps) {
  const { t, i18n } = useLocale();
  const stripe = useStripe();
  const elements = useElements();
  const [status, setStatus] = useState<PaymentUiStatus>("idle");
  const [errorKey, setErrorKey] = useState<StripePaymentErrorKey | null>(null);
  // Synchronous guard: `status` state does not update between two same-tick clicks, so a ref is
  // what actually prevents a double confirmation (US 1.5).
  const submittingRef = useRef(false);

  const isHold = props.paymentOption === "HOLD";

  // Keep the embedded card field in the patient's language.
  useEffect(() => {
    elements?.update({ locale: i18n.language as StripeElementLocale });
  }, [elements, i18n.language]);

  // US 1.8 — while the intent is still validating (processing / async / awaiting the PAY-E2 webhook),
  // poll the server for the authoritative outcome until a terminal status or timeout. The server —
  // never the browser — is the authority that declares the payment done.
  useStripePaymentStatusPolling({
    enabled: status === "validating" && typeof props.pollStatus === "function",
    fetchStatus: props.pollStatus ?? (async () => "payable" as PollableStatus),
    onResolved: (terminal) => {
      if (terminal === "paid") {
        setStatus("success");
        props.onPaid?.();
      } else {
        setErrorKey("payment_error_generic");
        setStatus("error");
      }
    },
    // Bounded: after ~2 minutes without a server-confirmed outcome, tell the patient instead of
    // spinning forever (the webhook keeps confirming in the background; email follows).
    onTimeout: () => setStatus("timeout"),
    maxAttempts: 10,
  });

  const handleSubmit = async (ev: SyntheticEvent) => {
    ev.preventDefault();
    // Double-submit guard (US 1.5): ignore clicks while a confirmation is in flight or already done.
    if (!stripe || !elements || submittingRef.current || status === "success") {
      return;
    }
    submittingRef.current = true;
    setStatus("processing");
    setErrorKey(null);

    const confirmParams = { return_url: props.returnUrl };
    const result = isHold
      ? await stripe.confirmSetup({ elements, redirect: "if_required", confirmParams })
      : await stripe.confirmPayment({ elements, redirect: "if_required", confirmParams });

    if (result.error) {
      // US 1.3 — generic localised message, Booking preserved, retry allowed on the same clientSecret.
      setErrorKey(getStripePaymentErrorKey(result.error));
      setStatus("error");
      submittingRef.current = false;
      return;
    }

    const intent =
      ("paymentIntent" in result ? result.paymentIntent : undefined) ??
      ("setupIntent" in result ? result.setupIntent : undefined);

    switch (intent?.status) {
      case "succeeded":
        // The browser-side success is a strong hint, not the authority: when a server status reader is
        // available, wait for PAY-E2 to have flipped the booking (observed via the poller, first read
        // is immediate) before signalling onPaid — so the success redirect never races the webhook.
        if (typeof props.pollStatus === "function") {
          setStatus("validating");
        } else {
          setStatus("success");
          props.onPaid?.();
        }
        break;
      case "processing":
        setStatus("validating");
        break;
      case "requires_payment_method":
        setErrorKey("payment_error_generic");
        setStatus("error");
        submittingRef.current = false;
        break;
      default:
        // requires_action is handled in-place by stripe.js; anything else → observe (validating).
        setStatus("validating");
        break;
    }
  };

  if (status === "validating") {
    return (
      <div className="bg-subtle mt-4 rounded-md p-6 text-center" role="status" aria-live="polite">
        {props.recap}
        <div className="text-emphasis font-semibold">{t("payment_validating")}</div>
        <p className="text-subtle mt-2 text-sm">{t("payment_validating_description")}</p>
      </div>
    );
  }

  // US 1.8 — the poller gave up before observing a server-confirmed outcome. The payment may still
  // complete in the background (webhook + confirmation email); never leave the patient on an
  // unbounded spinner with no explanation or exit.
  if (status === "timeout") {
    return (
      <div
        className="bg-subtle mt-4 rounded-md p-6 text-center"
        role="status"
        aria-live="polite"
        data-testid="stripe-payment-timeout">
        {props.recap}
        <div className="text-emphasis font-semibold">{t("payment_pending_confirmation")}</div>
        <p className="text-subtle mt-2 text-sm">{t("payment_pending_confirmation_description")}</p>
        {props.bookingHref && (
          <div className="mt-4 flex justify-center">
            <Button color="secondary" href={props.bookingHref}>
              {t("view_booking")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <form id="stripe-inline-payment-form" className="bg-subtle mt-4 rounded-md p-6" onSubmit={handleSubmit}>
      {props.recap}

      <div className="text-emphasis mb-4 flex items-center justify-between font-semibold">
        <span>{isHold ? t("no_show_fee") : t("price")}</span>
        <Price currency={props.currency} price={props.amount} displayAlternateSymbol={false} />
      </div>

      <PaymentElement
        options={PAYMENT_ELEMENT_OPTIONS}
        onChange={() => {
          // Any edit clears a previous error and re-enables the pay button.
          if (status === "error") {
            setStatus("idle");
            setErrorKey(null);
          }
        }}
      />

      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          color="primary"
          data-testid="stripe-pay-button"
          loading={status === "processing"}
          disabled={!stripe || status === "processing" || status === "success"}>
          {isHold ? t("submit_card") : t("pay_now")}
        </Button>
      </div>

      {status === "success" && (
        <div className="text-subtle mt-4 text-center text-sm" role="status" aria-live="polite">
          {t("payment_success_redirecting")}
        </div>
      )}

      {errorKey && (
        <div className="mt-4 text-center text-sm text-error" role="alert">
          {t(errorKey)}
        </div>
      )}
    </form>
  );
}

export function StripeInlinePayment(props: StripeInlinePaymentProps) {
  const { publishableKey, stripeAccount, clientSecret, isDarkTheme } = props;

  // Direct, per-account load (multi-tenant Connect) — memoised so we don't reload on every render.
  const stripePromise = useMemo(
    () => loadStripe(publishableKey, { stripeAccount }),
    [publishableKey, stripeAccount]
  );

  const [dark, setDark] = useState<boolean>(isDarkTheme ?? false);
  useEffect(() => {
    if (typeof isDarkTheme === "boolean") {
      setDark(isDarkTheme);
      return;
    }
    if (typeof document !== "undefined") {
      setDark(document.documentElement.classList.contains("dark"));
    }
  }, [isDarkTheme]);

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance: { theme: dark ? "night" : "stripe" } }}>
      <StripeInlinePaymentForm {...props} />
    </Elements>
  );
}

export default StripeInlinePayment;
